import useHost from "containers/host/useHooks";
import useOutsideClick from "hooks/click-outside";
import React from "react";
import { AiOutlineClose } from "react-icons/ai";
import { useForm } from "react-hook-form";

interface CancelEventModalProps {
  isOpen: boolean;
  onClose: () => void;
  eventName: string;
  eventId: number;
}

const CancelEventModal: React.FC<CancelEventModalProps> = ({
  isOpen,
  onClose,
  eventName,
  eventId,
}) => {
  const { cancelEvent } = useHost();
  const [reason, setReason] = React.useState("");
  const modalRef = useOutsideClick(onClose);
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<{ reason: string }>({
    defaultValues: { reason: "" },
  });

  if (!isOpen) return null;

  const onSubmit = async (data: { reason: string }) => {
    await cancelEvent(eventId, { reason: data.reason });
    reset();
    onClose();
  };

  return (
    <div
      className={`fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center transition-opacity duration-300 ${
        isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
      }`}
      style={{ zIndex: 'calc(var(--z-modal) + 1)' }}
    >
      <div
        ref={modalRef}
        className={`relative bg-white px-6 py-8 rounded-lg shadow-lg w-[90%] sm:w-[380px] h-auto transform transition-transform duration-300 ${
          isOpen ? "scale-100" : "scale-95"
        }`}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute right-4 top-4 text-gray-500 hover:text-gray-700"
        >
          <AiOutlineClose size={22} />
        </button>

        {/* Title */}
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Cancel Event</h2>

        {/* Event Name Box */}
        <div className="w-full bg-gray-100 border rounded-xl flex items-center gap-3 px-4 py-3 mb-4">
          <div className="bg-gray-200 p-2 rounded-md">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="w-5 h-5 text-gray-700"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="1.5"
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2v-9a2 2 0 00-2-2H5a2 2 0 00-2 2v9a2 2 0 002 2z"
              />
            </svg>
          </div>

          <span className="text-gray-900 font-medium">{eventName}</span>
        </div>

        {/* Warning Box */}
        <div className="bg-orange-50 border border-orange-300 text-orange-700 rounded-xl p-4 mb-5 text-sm">
          <div className="flex items-start gap-2">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="w-5 h-5 mt-0.5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="1.5"
                d="M12 9v3m0 4h.01M10.29 3.86l-7.24 12.5A1.5 1.5 0 003.36 19h17.28a1.5 1.5 0 001.31-2.64l-7.24-12.5a1.5 1.5 0 00-2.62 0z"
              />
            </svg>

            <p>
              Cancellation requires admin approval. You will be notified once
              your request is reviewed.
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit(onSubmit)}>
          <label className="text-label text-base font-semibold mb-1 block">
            Reason for Cancellation
          </label>

          <textarea
            {...register("reason", {
              required: "Reason is required",
              minLength: {
                value: 10,
                message: "Reason must be at least 10 characters",
              },
              maxLength: {
                value: 500,
                message: "Maximum 500 characters allowed",
              },
            })}
            className={`w-full min-h-[120px] p-4 rounded-lg border border-inputsBorderColor
             outline-none`}
            placeholder="Please provide a detailed explanation..."
          />

          {/* ✅ Error Message */}
          {errors.reason && (
            <p className="text-red-500 text-sm mt-1">{errors.reason.message}</p>
          )}

          <div className="flex items-center justify-between gap-4 mt-6">
            <button
              onClick={onClose}
              type="button"
              className="w-1/2 border border-gray-300 text-gray-700 font-semibold py-3 rounded-xl hover:bg-gray-100"
            >
              Cancel
            </button>

            <button
              type="submit"
              className="w-1/2 bg-red-500 text-white font-semibold py-3 rounded-xl hover:bg-red-600"
            >
              Submit Request
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CancelEventModal;
